// ── CategoryTreePicker ────────────────────────────────────────────────────
// Reusable admin component: renders the category tree as a collapsible tree
// with checkboxes. Used for event targeting, hive group signals, ad placement.
//
// Props:
//   selected    — Set of selected leaf ids
//   onChange    — (newSet) => void
//   lang        — 'en' | 'fr'
//   users       — optional: array of users to show match count
//   compact     — bool: show compact version without match counts
//   maxDepth    — how deep to render (default: all)

import { useState, useCallback } from 'react';
import { buildCategoryTree, getLabel, countMatches } from '../utils/categoryTree';

const G = {
  bg:      '#F4F2EF',
  card:    '#FFFFFF',
  border:  '#E5E2DC',
  borderMd:'#D0CCC4',
  white:   '#1A1714',
  muted:   '#8C8680',
  sub:     '#5C5650',
  gold:    '#C9A84C',
  goldBg:  'rgba(201,168,76,0.08)',
  goldBdr: 'rgba(201,168,76,0.35)',
  green:   '#1A7A4A',
  red:     '#C0392B',
  sans:    "'Outfit',sans-serif",
};

// ── Single tree node ──────────────────────────────────────────────────────
function TreeNode({ node, selected, onChange, lang, users, depth = 0 }) {
  const isLeaf = !node.children || node.children.length === 0;
  const isSelected = selected.has(node.id);
  const [open, setOpen] = useState(depth < 2);

  // For non-leaf nodes: compute partial/full selection state
  const leafIds = [];
  function collectLeaves(n) {
    if (!n.children || n.children.length === 0) leafIds.push(n.id);
    else n.children.forEach(collectLeaves);
  }
  if (!isLeaf) collectLeaves(node);
  const selectedCount = leafIds.filter(id => selected.has(id)).length;
  const allSelected   = leafIds.length > 0 && selectedCount === leafIds.length;
  const partial       = selectedCount > 0 && selectedCount < leafIds.length;

  const handleToggle = () => {
    const newSet = new Set(selected);
    if (isLeaf) {
      if (isSelected) newSet.delete(node.id);
      else newSet.add(node.id);
    } else {
      // Toggle all leaves under this node
      if (allSelected) leafIds.forEach(id => newSet.delete(id));
      else leafIds.forEach(id => newSet.add(id));
    }
    onChange(newSet);
  };

  const matchCount = users && isLeaf && node.match ? users.filter(node.match).length : null;
  const label = getLabel(node, lang);
  const indent = depth * 14;

  return (
    <div>
      <div
        style={{
          display:'flex', alignItems:'center', gap:'0.5rem',
          padding:`0.3rem 0.5rem 0.3rem ${indent + 8}px`,
          borderRadius:6, cursor:'pointer',
          background: isSelected || allSelected ? G.goldBg : 'transparent',
          transition:'background 0.1s',
        }}
        onMouseEnter={e=>e.currentTarget.style.background='rgba(0,0,0,0.03)'}
        onMouseLeave={e=>e.currentTarget.style.background=(isSelected||allSelected)?G.goldBg:'transparent'}
      >
        {/* Expand arrow (non-leaf) */}
        {!isLeaf && (
          <span
            onClick={e=>{e.stopPropagation(); setOpen(o=>!o);}}
            style={{ width:14, height:14, display:'flex', alignItems:'center', justifyContent:'center', color:G.muted, fontSize:'0.6rem', flexShrink:0, cursor:'pointer', userSelect:'none' }}>
            {open ? '▾' : '▸'}
          </span>
        )}
        {isLeaf && <span style={{ width:14, flexShrink:0 }}/>}

        {/* Checkbox */}
        <div
          onClick={handleToggle}
          style={{
            width:15, height:15, borderRadius:3, flexShrink:0, cursor:'pointer',
            border:`1.5px solid ${allSelected||isSelected ? G.gold : partial ? G.gold+'99' : G.borderMd}`,
            background: allSelected||isSelected ? G.gold : partial ? G.goldBg : 'transparent',
            display:'flex', alignItems:'center', justifyContent:'center',
            transition:'all 0.15s',
          }}>
          {(allSelected || isSelected) && <span style={{ fontSize:'0.55rem', color:'#0A0A0A', fontWeight:900, lineHeight:1 }}>✓</span>}
          {partial && !allSelected && <span style={{ width:7, height:2, background:G.gold, display:'block', borderRadius:1 }}/>}
        </div>

        {/* Icon (top-level) */}
        {node.icon && <span style={{ fontSize:'0.85rem', lineHeight:1 }}>{node.icon}</span>}

        {/* Label */}
        <div onClick={handleToggle} style={{ flex:1, minWidth:0, cursor:'pointer', userSelect:'none' }}>
          <span style={{
            fontSize: depth===0 ? '0.82rem' : depth===1 ? '0.78rem' : '0.72rem',
            fontWeight: depth===0 ? 700 : depth===1 ? 600 : 400,
            color: isSelected||allSelected ? G.gold : partial ? G.sub : depth===0 ? G.white : depth===1 ? G.sub : G.muted,
            letterSpacing: depth===0 ? '0.04em' : '0',
          }}>
            {label}
          </span>
          {node.criteriaLabels && (
            <div style={{ fontSize:'0.6rem', color:G.muted, marginTop:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {node.criteriaLabels.slice(0, 4).join(' · ')}
              {node.criteriaLabels.length > 4 ? ` +${node.criteriaLabels.length - 4}` : ''}
            </div>
          )}
        </div>

        {/* Match count */}
        {matchCount !== null && (
          <span style={{ fontSize:'0.58rem', color: matchCount > 0 ? G.green : G.muted, background:'rgba(0,0,0,0.05)', padding:'0.05rem 0.35rem', borderRadius:99, flexShrink:0 }}>
            {matchCount}
          </span>
        )}
        {!isLeaf && selectedCount > 0 && (
          <span style={{ fontSize:'0.58rem', color:G.gold, background:'rgba(201,168,76,0.1)', padding:'0.05rem 0.35rem', borderRadius:99, flexShrink:0 }}>
            {selectedCount}/{leafIds.length}
          </span>
        )}
      </div>

      {/* Children */}
      {!isLeaf && open && (
        <div>
          {node.children.map(child => (
            <TreeNode key={child.id} node={child} selected={selected} onChange={onChange}
              lang={lang} users={users} depth={depth+1}/>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Selected chips summary ────────────────────────────────────────────────
function SelectedChips({ selected, onChange, lang, tree }) {
  if (selected.size === 0) return null;
  return (
    <div style={{ display:'flex', flexWrap:'wrap', gap:'0.3rem', marginTop:'0.6rem' }}>
      {[...selected].map(id => {
        const node = findNode(tree, id);
        if (!node) return null;
        return (
          <span key={id} style={{ display:'inline-flex', alignItems:'center', gap:'0.3rem', padding:'0.18rem 0.55rem', background:'rgba(201,168,76,0.12)', border:'1px solid rgba(201,168,76,0.35)', borderRadius:99, fontSize:'0.65rem', color:G.gold }}>
            {getLabel(node, lang)}
            <span onClick={() => { const s=new Set(selected); s.delete(id); onChange(s); }} style={{ cursor:'pointer', opacity:0.7, fontSize:'0.7rem', lineHeight:1 }}>×</span>
          </span>
        );
      })}
      <span onClick={() => onChange(new Set())} style={{ padding:'0.18rem 0.55rem', fontSize:'0.62rem', color:G.muted, cursor:'pointer', border:`1px solid ${G.border}`, borderRadius:99 }}>
        Clear all
      </span>

    </div>
  );
}

function findNode(node, id) {
  if (node.id === id) return node;
  if (!node.children) return null;
  for (const c of node.children) { const f = findNode(c, id); if (f) return f; }
  return null;
}

// ── Main export ───────────────────────────────────────────────────────────
export default function CategoryTreePicker({ selected: selectedProp, onChange: onChangeProp, lang = 'en', users = null, compact = false, title = 'Target audience' }) {
  // Standalone mode: manage own state when no props are passed
  const [internalSelected, setInternalSelected] = useState(new Set());
  const isControlled = selectedProp !== undefined && onChangeProp !== undefined;
  const selected  = isControlled ? selectedProp  : internalSelected;
  const onChange  = isControlled ? onChangeProp  : setInternalSelected;

  // Build tree on every render — picks up newly saved audiences from localStorage
  const tree = buildCategoryTree();
  const totalMatch = users ? countMatches(users, [...selected]) : null;

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.6rem' }}>
        <span style={{ fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:G.muted }}>{title}</span>
        <div style={{ display:'flex', alignItems:'center', gap:'0.6rem' }}>
          {selected.size > 0 && (
            <span style={{ fontSize:'0.65rem', color:G.gold }}>
              {selected.size} criteria
              {totalMatch !== null && <span style={{ color:G.green }}> · {totalMatch} users match</span>}
            </span>
          )}
          {selected.size === 0 && users && (
            <span style={{ fontSize:'0.65rem', color:G.muted }}>All {users.length} users (no filter)</span>
          )}
        </div>
      </div>

      {/* Tree */}
      <div style={{ background:G.card, border:`1px solid ${G.border}`, borderRadius:8, padding:'0.4rem', maxHeight:compact ? 320 : 480, overflowY:'auto', boxShadow:'inset 0 1px 3px rgba(0,0,0,0.04)' }}>
        {tree.children.map(domain => (
          <TreeNode key={domain.id} node={domain} selected={selected} onChange={onChange}
            lang={lang} users={users} depth={0}/>
        ))}
      </div>

      {/* Selected summary */}
      <SelectedChips selected={selected} onChange={onChange} lang={lang} tree={tree}/>
    </div>
  );
}
